import React, { useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import CorporateEventsService, { RegistrationCustomQuestion } from '~/services/CorporateEventsService';

import RegistrationCustomQuestionDialog from './RegistrationCustomQuestionDialog';
import RegistrationCustomQuestionsList from './RegistrationCustomQuestionsList';

interface RegistrationCustomQuestionsProps {
  eventId?: string;
  questions: Array<RegistrationCustomQuestion>;
  onQuestionsChange: (questions: Array<RegistrationCustomQuestion>) => void;
}

export default function RegistrationCustomQuestions({
  eventId,
  questions,
  onQuestionsChange,
}: RegistrationCustomQuestionsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState<RegistrationCustomQuestion | null>(null);

  // Calculate next sortOrder based on existing questions
  const nextSortOrder = useMemo(() => {
    let allQuestions: Array<RegistrationCustomQuestion> = [];

    if (eventId) {
      // Get questions from React Query cache
      allQuestions =
        queryClient.getQueryData<Array<RegistrationCustomQuestion>>([
          'corporate-events',
          'registration-custom-questions',
          eventId,
        ]) || [];
    } else {
      allQuestions = questions;
    }

    const maxSortOrder = allQuestions.reduce((max, q) => {
      const sortOrder = q.sortOrder || 0;
      return sortOrder > max ? sortOrder : max;
    }, 0);

    return maxSortOrder + 1;
  }, [eventId, queryClient, questions]);

  const createMutation = useMutation({
    mutationFn: ({
      questionData,
      options: _options,
    }: {
      questionData: Parameters<typeof CorporateEventsService.createRegistrationCustomQuestion>[0];
      options?: Array<NonNullable<RegistrationCustomQuestion['options']>[number]>;
    }) => CorporateEventsService.createRegistrationCustomQuestion(questionData),
    onSuccess: async (createdQuestion, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'registration-custom-questions', eventId] });

      if (variables.options && variables.options.length > 0) {
        for (const option of variables.options) {
          await CorporateEventsService.createRegistrationCustomQuestionOption({
            questionId: createdQuestion.id,
            label: option.label,
            value: option.value,
            sortOrder: option.sortOrder,
          });
        }

        // Invalidate options query to ensure fresh data when dialog reopens
        queryClient.invalidateQueries({
          queryKey: ['corporate-events', 'registration-custom-question-options', createdQuestion.id],
        });
      }

      enqueueSnackbar('Question created successfully', { variant: 'success' });
      setQuestionDialogOpen(false);
      setQuestionToEdit(null);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating question: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
      optionsDiff: _optionsDiff,
    }: {
      id: string;
      data: Parameters<typeof CorporateEventsService.updateRegistrationCustomQuestion>[1];
      optionsDiff?: RegistrationCustomQuestion['optionsDiff'];
    }) => CorporateEventsService.updateRegistrationCustomQuestion(id, data),
    onSuccess: async (updatedQuestion, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'registration-custom-questions', eventId] });

      if (variables.optionsDiff && variables.id) {
        try {
          const { toCreate, toUpdate, toDelete } = variables.optionsDiff;

          // Delete removed options
          for (const optionToDelete of toDelete) {
            if (optionToDelete.id) {
              await CorporateEventsService.deleteRegistrationCustomQuestionOption(optionToDelete.id);
            }
          }

          // Create new options
          for (const optionToCreate of toCreate) {
            await CorporateEventsService.createRegistrationCustomQuestionOption({
              questionId: variables.id,
              label: optionToCreate.label,
              value: optionToCreate.value,
              sortOrder: optionToCreate.sortOrder,
            });
          }

          // Update modified options
          for (const optionToUpdate of toUpdate) {
            if (optionToUpdate.id) {
              await CorporateEventsService.updateRegistrationCustomQuestionOption(optionToUpdate.id, {
                label: optionToUpdate.label,
                value: optionToUpdate.value,
                sortOrder: optionToUpdate.sortOrder,
              });
            }
          }

          // Invalidate options query to ensure fresh data when dialog reopens
          queryClient.invalidateQueries({
            queryKey: ['corporate-events', 'registration-custom-question-options', variables.id],
          });
        } catch (error) {
          enqueueSnackbar(`Question updated but failed to update some options: ${(error as Error).message}`, {
            variant: 'warning',
          });
        }
      }

      enqueueSnackbar('Question updated successfully', { variant: 'success' });
      setQuestionDialogOpen(false);
      setQuestionToEdit(null);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating question: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSaveQuestion = async (question: RegistrationCustomQuestion) => {
    if (!eventId) {
      // For create mode, just update local state
      const uniqueQuestions = Array.from(
        new Map([...questions, question].map((item) => [item.id || `${item.label}-${item.fieldName}`, item])).values(),
      );
      onQuestionsChange(uniqueQuestions);
      const alreadyExists = questions.some((q) => q.id === question.id);
      enqueueSnackbar(`Question ${alreadyExists ? 'updated' : 'created'}. It will be saved when you save the event.`, {
        variant: 'success',
      });
      setQuestionDialogOpen(false);
      setQuestionToEdit(null);
      return;
    }

    // For edit mode, save directly to API
    if (question.id) {
      await updateMutation.mutateAsync({
        id: question.id,
        data: {
          label: question.label,
          description: question.description,
          fieldName: question.fieldName,
          fieldType: question.fieldType,
          isRequired: question.isRequired,
          sortOrder: question.sortOrder,
          isSensitive: question.isSensitive,
        },
        optionsDiff: question.optionsDiff,
      });
    } else {
      await createMutation.mutateAsync({
        questionData: {
          eventId,
          label: question.label,
          description: question.description,
          fieldName: question.fieldName,
          fieldType: question.fieldType,
          isRequired: question.isRequired,
          sortOrder: question.sortOrder,
          isSensitive: question.isSensitive,
        },
        options: question.options,
      });
    }
  };

  const handleCreateClick = () => {
    setQuestionToEdit(null);
    setQuestionDialogOpen(true);
  };

  const handleEditClick = (question: RegistrationCustomQuestion) => {
    setQuestionToEdit(question);
    setQuestionDialogOpen(true);
  };

  const handleClose = () => {
    setQuestionDialogOpen(false);
    setQuestionToEdit(null);
  };

  return (
    <>
      <RegistrationCustomQuestionsList
        eventId={eventId}
        questions={questions}
        onQuestionsChange={onQuestionsChange}
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
      />
      <RegistrationCustomQuestionDialog
        eventId={eventId}
        open={questionDialogOpen}
        question={questionToEdit}
        defaultSortOrder={nextSortOrder}
        onClose={handleClose}
        onSave={handleSaveQuestion}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
}
